import { useState, useMemo } from "react";
import { useParams, Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Play, Loader2, AlertTriangle } from "lucide-react";

type NodeBreakdown = {
  nodeId: number;
  label: string | null;
  type: string;
  subtype: string;
  config: any;
  counts: Record<string, number>;
};

const STATUS_LABEL: Record<string, string> = {
  paused: "Pausadas",
  running: "Rodando",
  completed: "Completas",
  failed: "Falhou",
  pending: "Pendente",
};

export default function FlowExecutionsPage() {
  const params = useParams();
  const flowId = Number(params.id);

  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("paused");
  const [requireDelivered, setRequireDelivered] = useState(false);
  const [sourceHandle, setSourceHandle] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const utils = trpc.useUtils();
  const flowQ = trpc.flows.get.useQuery({ id: flowId }, { enabled: !!flowId });
  const overviewQ = trpc.flows.executionsByNode.useQuery({ flowId }, { enabled: !!flowId });

  const listQ = trpc.flows.listExecutionsAtNode.useQuery(
    {
      flowId,
      nodeId: selectedNodeId ?? 0,
      status: statusFilter === "all" ? undefined : (statusFilter as any),
      requireDelivered,
      limit: 200,
      offset: 0,
    },
    { enabled: !!flowId && !!selectedNodeId },
  );

  const advanceMutation = trpc.flows.bulkAdvanceExecutions.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.advanced} avançada(s), ${data.failed} falha(s) (elegíveis: ${data.eligible})`);
      utils.flows.executionsByNode.invalidate({ flowId });
      utils.flows.listExecutionsAtNode.invalidate();
      setConfirmOpen(false);
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const dryRunMutation = trpc.flows.bulkAdvanceExecutions.useMutation({
    onSuccess: (data) => toast.info(`Simulação: ${data.eligible} contato(s) elegíveis para avanço`),
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const selectedNode: NodeBreakdown | undefined = useMemo(
    () => overviewQ.data?.nodes.find((n) => n.nodeId === selectedNodeId),
    [overviewQ.data, selectedNodeId],
  );

  const buttonOptions = useMemo<Array<{ value: string; label: string }>>(() => {
    const cfg = selectedNode?.config as any;
    const buttons = cfg?.buttons ?? cfg?.routableButtons ?? [];
    if (!Array.isArray(buttons)) return [];
    return buttons.map((b: any, idx: number) => ({
      value: b.id ? `btn-${b.id}` : `default`,
      label: b.text ?? b.label ?? `Botão ${idx + 1}`,
    }));
  }, [selectedNode]);

  const handleNodeClick = (nodeId: number) => {
    setSelectedNodeId(nodeId);
    setSourceHandle("");
  };

  const handleBulkAdvance = (dryRun: boolean) => {
    if (!selectedNodeId) return;
    const opts = {
      flowId,
      nodeId: selectedNodeId,
      sourceHandle: sourceHandle || undefined,
      requireDelivered,
      dryRun,
    };
    if (dryRun) dryRunMutation.mutate(opts);
    else advanceMutation.mutate(opts);
  };

  const totals = overviewQ.data?.totals;

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-2">
          <Link href={flowQ.data?.channel === "whatsapp" ? "/whatsapp-flows" : "/flows"}>
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Voltar
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold">{flowQ.data?.name ?? "Execuções do fluxo"}</h1>
            <p className="text-sm text-muted-foreground">Contatos por nó — avance manualmente quem ficou parado.</p>
          </div>
        </div>

        {totals && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(["paused", "running", "completed", "failed"] as const).map((s) => (
              <Card key={s}>
                <CardContent className="py-4">
                  <div className="text-xs text-muted-foreground uppercase">{STATUS_LABEL[s]}</div>
                  <div className="text-2xl font-semibold">{totals[s]}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Nós com execuções</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {overviewQ.isLoading && <div className="text-sm text-muted-foreground">Carregando…</div>}
              {!overviewQ.isLoading && (overviewQ.data?.nodes.length ?? 0) === 0 && (
                <div className="text-sm text-muted-foreground">Nenhuma execução encontrada para esse fluxo.</div>
              )}
              {overviewQ.data?.nodes.map((n) => (
                <button
                  key={n.nodeId}
                  onClick={() => handleNodeClick(n.nodeId)}
                  className={`w-full text-left rounded-md border p-3 transition hover:bg-accent ${selectedNodeId === n.nodeId ? "border-primary bg-accent/50" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{n.label || `Nó #${n.nodeId}`}</div>
                      <div className="text-xs text-muted-foreground">{n.type} / {n.subtype}</div>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end">
                      {Object.entries(n.counts).map(([status, count]) => (
                        <Badge key={status} variant={status === "paused" ? "default" : "secondary"} className="text-xs">
                          {STATUS_LABEL[status] ?? status}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {selectedNode ? `Contatos no nó "${selectedNode.label || `#${selectedNode.nodeId}`}"` : "Selecione um nó"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!selectedNodeId && (
                <div className="text-sm text-muted-foreground">Clique em um nó à esquerda para ver os contatos.</div>
              )}

              {selectedNodeId && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Status</Label>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paused">Pausadas</SelectItem>
                          <SelectItem value="running">Rodando</SelectItem>
                          <SelectItem value="failed">Falhou</SelectItem>
                          <SelectItem value="completed">Completas</SelectItem>
                          <SelectItem value="all">Todas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex items-center gap-2">
                        <Switch id="delivered" checked={requireDelivered} onCheckedChange={setRequireDelivered} />
                        <Label htmlFor="delivered" className="text-xs">Apenas com mensagem entregue</Label>
                      </div>
                    </div>
                  </div>

                  {statusFilter === "paused" && (
                    <div className="rounded-md border bg-amber-50 dark:bg-amber-950/30 p-3 space-y-3">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600" />
                        <div className="text-sm">
                          <strong>Avanço em massa:</strong> envia o próximo nó pulando o aguardo de botão. Use quando os contatos receberam a mensagem mas não clicaram.
                        </div>
                      </div>
                      {buttonOptions.length > 0 && (
                        <div>
                          <Label className="text-xs">Conexão de saída (botão)</Label>
                          <Select value={sourceHandle} onValueChange={setSourceHandle}>
                            <SelectTrigger><SelectValue placeholder="Padrão (primeira disponível)" /></SelectTrigger>
                            <SelectContent>
                              {buttonOptions.map((b) => (
                                <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleBulkAdvance(true)}
                          disabled={dryRunMutation.isPending || advanceMutation.isPending}
                        >
                          {dryRunMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                          Simular
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setConfirmOpen(true)}
                          disabled={advanceMutation.isPending}
                          className="gap-1"
                        >
                          <Play className="h-3 w-3" /> Avançar todos
                        </Button>
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="text-xs text-muted-foreground mb-2">
                      {listQ.isLoading ? "Carregando…" : `${listQ.data?.total ?? 0} execução(ões)`}
                    </div>
                    <div className="rounded-md border max-h-[400px] overflow-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Contato</TableHead>
                            <TableHead>Telefone</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Atualizada</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {listQ.data?.items.map((r) => (
                            <TableRow key={r.executionId}>
                              <TableCell>{r.contactName ?? `#${r.contactId}`}</TableCell>
                              <TableCell className="font-mono text-xs">{r.contactPhone ?? "—"}</TableCell>
                              <TableCell>
                                <Badge variant={r.status === "paused" ? "default" : "secondary"}>{r.status}</Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {r.updatedAt ? new Date(r.updatedAt).toLocaleString("pt-BR") : "—"}
                              </TableCell>
                            </TableRow>
                          ))}
                          {!listQ.isLoading && (listQ.data?.items.length ?? 0) === 0 && (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-6">
                                Nenhuma execução com esses filtros.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Avançar contatos pausados?</AlertDialogTitle>
            <AlertDialogDescription>
              Vai enviar o próximo nó do fluxo para todos os contatos pausados nesse nó
              {requireDelivered ? " que tiveram mensagem entregue pela Meta" : ""}
              {sourceHandle ? ` pela conexão "${buttonOptions.find(b => b.value === sourceHandle)?.label ?? sourceHandle}"` : " pela primeira conexão de saída"}.
              <br /><br />
              <strong>Atenção:</strong> contatos fora da janela de 24h da Meta podem falhar — só funciona se o conteúdo do próximo nó for entregável (texto livre só dentro da janela; templates funcionam sempre).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleBulkAdvance(false)}
              disabled={advanceMutation.isPending}
            >
              {advanceMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Avançar agora
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
